import { Dispatch, SetStateAction } from 'react';

import { Fade } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  ExperienceLevel,
  EXPERIENCE_LEVEL_LABELS,
} from '../../../../happypaws-shared/src/Experience/experience';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useUploadGalleryPhoto } from '../../../../hooks/sitter/useUploadGalleryPhoto';

export interface Step2State {
  profilePhoto: File | null;
  username: string;
  bio: string;
  education: string;
  experience: ExperienceLevel | null;
  galleryImages: (string | null)[];
  verification: string;
  city: string;
  state: string;
  zipCode: string;
}

interface StepTwoProps {
  currentStep: number;
  handleNext: () => void;
  handleBack: () => void;
  step2: Step2State;
  setStep2: Dispatch<SetStateAction<Step2State>>;
  errors: Record<string, string>;
  clearError: (key: string) => void;
}

const StepTwo = (props: StepTwoProps) => {
  const { currentStep, handleNext, handleBack, step2, setStep2, errors, clearError } = props;

  const { mutate: uploadGalleryPhoto, isPending: isUploading } = useUploadGalleryPhoto();

  const handleImageUpload = (index: number, file: File | null) => {
    if (!file) return;

    // Show blob URL as preview immediately
    const previewUrl = URL.createObjectURL(file);
    setStep2((prev) => {
      const newImages = [...prev.galleryImages];
      newImages[index] = previewUrl;
      return { ...prev, galleryImages: newImages };
    });

    // Upload and replace with real URL
    uploadGalleryPhoto(file, {
      onSuccess: (realUrl) => {
        setStep2((prev) => {
          const newImages = [...prev.galleryImages];
          newImages[index] = realUrl;
          return { ...prev, galleryImages: newImages };
        });
      },
    });
  };

  return (
    <Fade in={currentStep === 2} timeout={600}>
      <Box sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.8rem',
            fontWeight: 700,
            color: '#178B8E',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Everything that pet owners should know about you.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: '1rem',
            color: '#757575',
            mb: 5,
            textAlign: 'center',
          }}
        >
          Stand out and make it easy for pet owners to choose you.
        </Typography>

        <Stack spacing={3}>
          {/* Profile Photo Upload */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                border: '2px dashed #B8D8D8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundImage: step2.profilePhoto
                  ? `url(${URL.createObjectURL(step2.profilePhoto)})`
                  : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                '&:hover': {
                  borderColor: '#178B8E',
                  backgroundColor: '#F0F9F9',
                },
              }}
              component="label"
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setStep2((prev) => ({ ...prev, profilePhoto: e.target.files![0] }));
                  }
                }}
              />
              {!step2.profilePhoto && (
                <AddPhotoAlternateIcon sx={{ fontSize: 40, color: '#B8D8D8' }} />
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Username:</Typography>
              <TextField
                fullWidth
                placeholder="Write your username"
                value={step2.username}
                onChange={(e) => {
                  setStep2((prev) => ({ ...prev, username: e.target.value }));
                  clearError('username');
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                  },
                }}
              />
              {errors.username && (
                <Typography variant="caption" color="error">
                  {errors.username}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Bio */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Give us a short bio:</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Write bio here..."
              value={step2.bio}
              onChange={(e) => setStep2((prev) => ({ ...prev, bio: e.target.value }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                },
              }}
            />
          </Box>
          {/* Location */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Your location:</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#178B8E', mb: 2 }}>
              So pet owners near you can find you.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="City"
                value={step2.city}
                onChange={(e) => setStep2((prev) => ({ ...prev, city: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF', borderRadius: '8px' },
                }}
              />
              <TextField
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF', borderRadius: '8px' },
                }}
                placeholder="State"
                value={step2.state}
                onChange={(e) => setStep2((prev) => ({ ...prev, state: e.target.value }))}
              />
              <TextField
                sx={{
                  width: 140,
                  '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF', borderRadius: '8px' },
                }}
                placeholder="Zip Code"
                value={step2.zipCode}
                onChange={(e) => setStep2((prev) => ({ ...prev, zipCode: e.target.value }))}
              />
            </Box>
          </Box>

          {/* Education and Experience */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Education:</Typography>
              <TextField
                fullWidth
                placeholder="Write your education"
                value={step2.education}
                onChange={(e) => setStep2((prev) => ({ ...prev, education: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Experience in pet sitting:</Typography>
              <FormControl fullWidth>
                <Select
                  value={step2.experience}
                  displayEmpty
                  renderValue={(value) =>
                    value ? (
                      EXPERIENCE_LEVEL_LABELS[value as ExperienceLevel]
                    ) : (
                      <span style={{ color: '#9e9e9e' }}>Select experience</span>
                    )
                  }
                  onChange={(e) => {
                    setStep2((prev) => ({
                      ...prev,
                      experience: e.target.value as ExperienceLevel,
                    }));
                    clearError('experience');
                  }}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                  }}
                >
                  {Object.entries(EXPERIENCE_LEVEL_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.experience && (
                <Typography variant="caption" color="error">
                  {errors.experience}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Gallery Upload */}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Gallery upload:</Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#178B8E',
                mb: 2,
              }}
            >
              Show pet owners who you are. (optional)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              {step2.galleryImages.map((image, index) => (
                <Box
                  key={index}
                  component="label"
                  sx={{
                    flex: 1,
                    height: 120,
                    border: '2px dashed #B8D8D8',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundImage: image ? `url(${image})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: image ? '#F0F9F9' : '#FFFFFF',
                    '&:hover': {
                      borderColor: '#178B8E',
                      backgroundColor: '#F0F9F9',
                    },
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleImageUpload(index, e.target.files[0]);
                      }
                    }}
                  />
                  {!image && <AddPhotoAlternateIcon sx={{ fontSize: 30, color: '#B8D8D8' }} />}
                </Box>
              ))}
            </Box>
            {isUploading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, textAlign: 'center' }}
              >
                Uploading...
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#178B8E',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Upload more
            </Typography>
          </Box>

          {/* Verification */}
          {/* <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Verification:</Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#178B8E',
                mb: 2,
              }}
            >
              Verify your identity to gain more trust from pet owners. (optional)
            </Typography>

            <FormControl fullWidth>
              <Select
                value={step2.verification}
                onChange={(e) => setStep2((prev) => ({ ...prev, verification: e.target.value }))}
                displayEmpty
                sx={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                }}
              >
                <MenuItem value="" disabled>
                  Verify identity
                </MenuItem>
                <MenuItem value="id">Government ID</MenuItem>
                <MenuItem value="background">Background Check</MenuItem>
                <MenuItem value="references">Professional References</MenuItem>
              </Select>
            </FormControl>
          </Box> */}
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              borderColor: '#178B8E',
              color: '#178B8E',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#0F6164',
                backgroundColor: '#F0F9F9',
              },
            }}
          >
            Back
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleNext}
            sx={{
              backgroundColor: '#178B8E',
              color: '#FFFFFF',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#0F6164',
              },
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default StepTwo;
